import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { getClientId } from 'src/app/models/client.util';
import { ClientServicesService } from 'src/app/services/admin/client-services.service';
import { SnackService } from 'src/app/services/snack.service';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiResponse } from 'src/app/models/api-response.model';
import { NANP_NON_US_REGION_NAMES, PhoneConfigEntry } from 'src/app/shared/models/phone-config.model';
import { twilioPhoneNumberValidator } from './twilio-phone.utils';

/** Ceiling for a backend message on screen. Past this it is skimmed rather than read. */
const BACKEND_MESSAGE_MAX_LENGTH = 200;

/**
 * The refusals whose text this dialog repeats to the user.
 *
 * `PhoneService` and `ClientMembershipGuard` write all four themselves — invalid number, no access,
 * client or entry not found, duplicate number. Their neighbours in the 4xx range are not authored for
 * a reader: see `backendMessage`.
 */
const BACKEND_MESSAGE_STATUSES: ReadonlyArray<number | undefined> = [400, 403, 404, 409];

/**
 * Longer than the 3s default, and paired with a Dismiss button.
 *
 * The reason also persists next to the actions, so this is the secondary channel — but a backend
 * message can run to a couple of lines and three seconds is not enough to read one.
 */
const SAVE_ERROR_SNACK_DURATION_MS = 8000;

@Component({
  selector: 'app-twilio-client-config-dialog',
  templateUrl: './twilio-client-config-dialog.component.html',
  styleUrls: ['./twilio-client-config-dialog.component.scss']
})
export class TwilioClientConfigDialogComponent implements OnInit {
  configForm: UntypedFormGroup;
  isLoading = false;
  isSaving = false;
  clientId: string;

  /** Whether the retired-line history is expanded. Collapsed by default: it is reference, not work. */
  showRetired = false;

  /**
   * Whether the load failed at all, for any reason.
   *
   * This is the flag the template gates on, not `loadDenied`. An empty `entries` means "no lines" or
   * "we never got to see them", and only a successful load tells them apart — the reason for the
   * failure does not. Keying the empty state on the 403 alone left every other failure, including the
   * 500 the membership guard itself throws when its Firestore read fails, still asserting the clinic
   * had no numbers with Add and Save live.
   *
   * That is worse than a wrong message. Adding a line to a clinic whose existing line the GET never
   * returned writes a second active entry: `keepOnlyActive` can only retire rows it loaded, so the
   * one-active-line rule silently breaks and the clinic may keep calling from the old number while
   * the dialog reports success.
   */
  loadFailed = false;

  /**
   * Whether that failure was specifically a refusal for lack of access.
   *
   * Only chooses the wording. "No access" and "could not load, try again" call for different actions
   * from the user, and telling someone to check permissions after a network blip sends them chasing
   * the wrong thing.
   */
  loadDenied = false;

  /**
   * Why the last save was refused, kept on screen until the next attempt.
   *
   * The snack bar alone was not enough: it clears itself after three seconds while the dialog stays
   * open by design, so the admin is left looking at the form that failed with no reason in sight — the
   * exact complaint this fix answers. Null when nothing has failed since the last attempt.
   */
  saveError: string | null = null;

  /**
   * Every line retired by adding rows, kept so discarding those rows can put them back.
   *
   * A list, not a slot. A single reference only ever covered the most recent add: two adds in a row
   * overwrote the pointer to the first retired line, and discarding both left it retired — which
   * `hasChanges` then dutifully persisted. A client with several active lines, which is legal data
   * today, lost all but one the same way.
   */
  private retiredByAdd: AbstractControl[] = [];

  private initialEntries = new Map<string, PhoneConfigEntry>();

  constructor(
    private fb: UntypedFormBuilder,
    private clientService: ClientServicesService,
    private snack: SnackService,
    public dialogRef: MatDialogRef<TwilioClientConfigDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data,
  ) {
    this.configForm = this.fb.group({
      entries: this.fb.array([]),
    });
    this.clientId = getClientId(this.data?.client);
  }

  get entries(): UntypedFormArray {
    return this.configForm.get('entries') as UntypedFormArray;
  }

  /**
   * Whether the immutability rule has anything to apply to yet.
   *
   * The notice lives once under the dialog subtitle rather than under each number: it states a rule
   * about the form, not a fact about a particular line, and repeating it under every saved row was
   * noise the eye learns to skip. Hidden until something is saved, because before that there is
   * nothing it constrains.
   */
  get hasPersistedEntries(): boolean {
    return this.entries.controls.some((_, index) => this.isPersisted(index));
  }

  /**
   * How many lines are retired.
   *
   * Drives both the collapsed history section and whether it appears at all. Counted from the live
   * controls rather than from what was loaded, so a line retired in this session moves into the
   * history immediately, without a save and reload.
   */
  get retiredCount(): number {
    // Saved rows only, matching what the section actually renders. An unsaved row stays in the top
    // pass whatever its toggle says, so counting it here would advertise history that is not there.
    return this.entries.controls.filter((entry, index) => !entry.get('isActive')?.value && this.isPersisted(index)).length;
  }

  ngOnInit(): void {
    this.loadPhoneConfigs();
  }


  async loadPhoneConfigs(): Promise<void> {
    // Reset first, before the early return: leaving it after meant a re-entry with a cleared clientId
    // kept the previous attempt's notice on screen with nothing driving it.
    this.loadFailed = false;
    this.loadDenied = false;
    if (!this.clientId) {
      return;
    }
    this.isLoading = true;
    try {
      const configs = await this.clientService.getClientPhoneConfigs(this.clientId).toPromise();
      this.setEntries(configs ?? []);
    } catch (error) {
      console.error('Error loading phone configs:', error);
      // Every failure counts, and the reason only picks the wording. See `loadFailed`.
      this.loadFailed = true;
      this.loadDenied = error?.status === 403;
      // The rows of a previous successful load must not survive a failed reload. They would render
      // with live Active toggles next to a notice saying the numbers cannot be changed, and with Save
      // hidden there would be no way to persist what the user just flipped. Through `setEntries` so
      // the `initialEntries` baseline is dropped with them rather than left describing absent rows.
      this.setEntries([]);
      // The 403 keeps our own wording: the backend's "You do not have access to this client" says the
      // same thing less usefully, without naming what is inaccessible. Every other refusal defers to
      // the backend, which is the only party that knows why.
      this.snack.genericSnackBar(
        this.loadDenied
          ? 'You do not have access to this client\'s phone configuration'
          : this.backendMessage(error) ?? 'Failed to load phone configuration',
        ['error-snackbar'],
      );
    } finally {
      this.isLoading = false;
    }
  }

  addEntry(): void {
    this.entries.push(this.buildEntryGroup());
    // A new row starts active, and a clinic has one line, so adding one is replacing one.
    this.retiredByAdd = [...this.retiredByAdd, ...this.keepOnlyActive(this.entries.length - 1)];
  }

  /**
   * Enforces the one-active-line rule by retiring the others, rather than by refusing the change.
   *
   * A clinic calls from exactly one number, and the backend takes the first active line it finds —
   * so a second active row does not produce an error, it produces a silently unused number. Making
   * activation exclusive means the invalid state cannot be reached from this dialog at all.
   *
   * Retiring rather than blocking is the deliberate half. Blocking would force "deactivate the old
   * one, then activate the new one", which passes through a moment with no active line, and a save
   * in between would leave the clinic calling from the generic Welby number. Switching in one
   * gesture has no such window.
   *
   * Turning the last line *off* is left alone: a clinic with nothing configured is a real state,
   * and it is the one this dialog exists to fix.
   */
  onActiveToggled(index: number): void {
    if (this.entries.at(index)?.get('isActive')?.value) {
      this.keepOnlyActive(index);
    }
  }

  /**
   * Only ever removes a row that was never saved. Saved lines are deactivated, not deleted.
   *
   * Discarding also undoes the retirement that adding the row caused. Without that, "add a number,
   * change your mind, press X" left the clinic with **no** active line: `hasChanges` sees the
   * previously-active line flipped to false and dutifully PATCHes it on save, so a gesture the
   * admin reads as "cancel" moves every one of that clinic's calls onto the generic Welby number,
   * silently. Tracked by control reference rather than index, because `removeAt` shifts positions.
   */
  discardEntry(index: number): void {
    if (this.isPersisted(index)) {
      return;
    }

    this.entries.removeAt(index);

    // Keyed on the *result*, not on what the discarded row happened to hold. Checking the discarded
    // row's own `isActive` missed the case where it had been toggled off before being discarded, and
    // cleared the record anyway — leaving the clinic with nothing active and no way back.
    const stillHasActive = this.entries.controls.some((entry) => entry.get('isActive')?.value);
    if (!stillHasActive) {
      this.retiredByAdd.forEach((entry) => entry.get('isActive')?.setValue(true));
    }
    this.retiredByAdd = [];
  }

  isPersisted(index: number): boolean {
    return !!this.entries.at(index)?.get('id')?.value;
  }

  /**
   * A saved line with no derived region has no local presence anywhere — toll-free, premium-rate
   * and government ranges all land here, and BRD-INT-2026-001 forbids presenting a toll-free
   * number to a patient as the clinic's caller ID.
   */
  /**
   * A saved line whose area code belongs to another NANP country.
   *
   * Kept after the state controls were removed, and it matters more now, not less: `+1` is
   * NANP-wide, `+1 902` is Nova Scotia and `+1 345` is the Cayman Islands — whose derived region is
   * `KY`, exactly like Kentucky. The backend prefers a US line and only falls back to one of these
   * with a warn, so without something on screen an admin configures a clinic that will call its
   * patients from another country and gets no signal at all.
   */
  isNonUsRegion(index: number): boolean {
    const entry = this.entries.at(index);
    return this.isPersisted(index) && !!entry?.get('isoCountry')?.value && entry.get('isoCountry').value !== 'US';
  }

  /** Place name for a non-US line, so `KY` is not read as Kentucky. */
  nonUsPlaceName(index: number): string {
    const region = this.entries.at(index)?.get('region')?.value as string | null;
    return (region && NANP_NON_US_REGION_NAMES[region]) || 'another country';
  }

  isNonGeographic(index: number): boolean {
    return this.isPersisted(index) && !this.entries.at(index)?.get('region')?.value;
  }

  async onSubmit(): Promise<void> {
    if (this.configForm.invalid || !this.clientId || this.isSaving) {
      return;
    }

    // Dropped on every attempt, so a retry does not sit under the reason the previous one failed.
    this.saveError = null;

    const created = this.entries.controls.filter((control) => !control.get('id').value);
    // Activations before deactivations. The array is in stored insertion order, so switching between
    // two saved lines patched the older one first: deactivate, then activate. A failure in between
    // persisted *zero* active lines and sent every call to the generic Welby number. Sorted this way
    // the same failure leaves two active — degraded, but the clinic still calls from its own number.
    const changed = this.entries.controls
      .filter((control) => this.hasChanges(control as UntypedFormGroup))
      .sort((a, b) => Number(b.get('isActive').value) - Number(a.get('isActive').value));

    if (!created.length && !changed.length) {
      this.dialogRef.close();
      return;
    }

    this.isSaving = true;
    try {
      // Sequential on purpose: the backend mirrors the primary entry into the legacy
      // `phoneConfig` on every write, and that mirror depends on insertion order.
      for (const control of created) {
        const saved = await this.clientService
          .addClientPhoneConfig(this.clientId, {
            phoneNumber: control.get('phoneNumber').value,
            isActive: control.get('isActive').value,
          })
          .toPromise();

        // The returned id is written back so a retry does not re-POST a number that already
        // exists. Without it, a create that succeeds followed by a patch that fails left the
        // clinic with two active lines *and* no way to finish from this dialog: the row still
        // looked new, the retry re-created it, the backend rejected the duplicate, and the patch
        // never ran. Recording the id turns the retry into "send only what is missing".
        if (saved?.id) {
          control.get('id').setValue(saved.id);
          // Locked like a loaded row. Without this the number stayed editable while the X button
          // disappeared, so an admin fixing a typo before retrying lost the edit in silence: the row
          // is no longer in `created`, and the PATCH body carries only `isActive`.
          control.get('phoneNumber').disable();
          this.initialEntries.set(saved.id, saved);
        }
      }
      for (const control of changed) {
        await this.clientService
          .patchClientPhoneConfig(this.clientId, control.get('id').value, {
            isActive: control.get('isActive').value,
          })
          .toPromise();
      }
      this.snack.genericSnackBar('Phone configuration saved successfully', ['success-snackbar']);
      this.dialogRef.close(true);
    } catch (error) {
      console.error('Error saving phone config:', error);
      // The backend's own message, when it sent one. It is the only thing that says *why* the save was
      // refused — "already exists for this client", "must be a valid North American (NANP) phone
      // number" — and it used to reach the console and nowhere else, so four unrelated causes all read
      // as "Failed to save phone configuration" on screen.
      const reason = this.backendMessage(error) ?? 'Failed to save phone configuration';
      // Kept next to the actions, not only in the snack bar. The dialog deliberately stays open so the
      // work is not lost, and a 3-second toast leaves it open with the reason already gone — which is
      // how this was reported in the first place. The snack bar still fires for the case where the
      // admin's eyes are elsewhere, with a Dismiss button so a long message is not on a timer.
      this.saveError = reason;
      this.snack.genericSnackBar(reason, ['error-snackbar'], SAVE_ERROR_SNACK_DURATION_MS, 'Dismiss');
    } finally {
      this.isSaving = false;
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onKeyPress(event: KeyboardEvent): boolean {
    const charCode = event.key.charCodeAt(0);
    // Allow: numbers (0-9), plus sign (+), and control characters (backspace, delete, etc.)
    if (event.ctrlKey || event.altKey || event.metaKey) {
      return true;
    }
    if (charCode === 43) { // Plus sign
      return true;
    }
    if (charCode >= 48 && charCode <= 57) { // Numbers 0-9
      return true;
    }
    return false;
  }

  /**
   * The message the backend sent, when it is one worth showing.
   *
   * Every endpoint here answers through the standard envelope, so a refusal arrives as an
   * `ApiResponse` in `HttpErrorResponse.error` — and its `message` is the only thing that
   * distinguishes "already exists for this client" from "must be a valid North American (NANP) phone
   * number" from "you do not have access". The dialog used to drop all of it.
   *
   * Allow-listed by status rather than by range, because not every 4xx is written for a human to read.
   * The listed four are the ones `PhoneService` and `ClientMembershipGuard` author themselves; the two
   * excluded neighbours are framework text — Passport's 401 is the bare word "Unauthorized" while
   * `AuthInterceptor` is already logging the user out, and Throttler's 429 is literally
   * `ThrottlerException: Too Many Requests`. Both read worse than our own copy.
   *
   * 5xx is excluded because those messages are internal fallbacks ("Failed to retrieve phone
   * numbers"), not explanations. Note it is *not* because the backend scrubs them: `sanitizeMessage`
   * only replaces a 5xx whose text matches its unsafe patterns, and for a 4xx it returns before those
   * patterns are consulted at all — so a future `BadRequestException(err.message)` on the server would
   * put raw Firestore internals in this string. Nothing here would stop it, which is the argument for
   * keeping the allow-list narrow.
   *
   * Truncated because the length is not ours to trust: class-validator joins every failed constraint
   * with `; `.
   */
  private backendMessage(error: unknown): string | null {
    const response = error as (HttpErrorResponse & { error?: ApiResponse<never> }) | null;
    if (!BACKEND_MESSAGE_STATUSES.includes(response?.status)) {
      return null;
    }

    const message = response?.error?.message;
    if (typeof message !== 'string' || !message.trim()) {
      return null;
    }

    const trimmed = message.trim();
    if (trimmed.length <= BACKEND_MESSAGE_MAX_LENGTH) {
      return trimmed;
    }
    // Split by code points, not code units: slicing mid-surrogate would leave a replacement character
    // sitting in front of the ellipsis.
    return `${Array.from(trimmed).slice(0, BACKEND_MESSAGE_MAX_LENGTH - 1).join('')}…`;
  }

  /**
   * A client still on the single-object format needs no special case here: the backend hydrates that
   * object into a normal entry with a deterministic id, so it arrives through `GET /phones` like any
   * other line. An empty list therefore means the client genuinely has no lines.
   */
  /** Retires every line but `index`, and returns **all** of them so the caller can undo the lot. */
  private keepOnlyActive(index: number): AbstractControl[] {
    const retired: AbstractControl[] = [];

    this.entries.controls.forEach((entry, position) => {
      if (position !== index && entry.get('isActive')?.value) {
        entry.get('isActive').setValue(false);
        retired.push(entry);
      }
    });

    return retired;
  }

  private setEntries(configs: PhoneConfigEntry[]): void {
    this.initialEntries.clear();
    this.entries.clear();

    configs.forEach((entry) => {
      this.initialEntries.set(entry.id, entry);
      this.entries.push(this.buildEntryGroup(entry));
    });

    // Existing data predates the one-line rule and can carry several active lines. Left alone, the
    // dialog rendered two "Active" toggles directly under copy asserting the client calls from one
    // number, and `hasChanges` was false for both — so the one screen meant to fix that state could
    // not fix it, on any visit. Retiring the extras here makes the copy true and lets Save persist
    // the correction. Not recorded in `retiredByAdd`: this is a correction, not something to undo.
    const firstActive = this.entries.controls.findIndex((entry) => entry.get('isActive')?.value);
    if (firstActive >= 0) {
      this.keepOnlyActive(firstActive);
    }

  }

  private buildEntryGroup(entry?: PhoneConfigEntry): UntypedFormGroup {
    return this.fb.group({
      id: [entry?.id ?? null],
      // Immutable once saved: the backend derives `region` from it, and PATCH does not accept it.
      // Replacing a number means adding the new one and deactivating the old one.
      phoneNumber: [
        { value: entry?.phoneNumber ?? '', disabled: !!entry },
        [Validators.required, twilioPhoneNumberValidator()],
      ],
      isActive: [entry?.isActive ?? true],
      region: [entry?.region ?? null],
      isoCountry: [entry?.isoCountry ?? null],
    });
  }

  private hasChanges(group: UntypedFormGroup): boolean {
    const id = group.get('id').value;
    const initial = id ? this.initialEntries.get(id) : null;
    if (!initial) {
      return false;
    }
    // `isActive` is the only editable field left: a saved number is never re-pointed in place,
    // because call logs already reference it.
    return group.get('isActive').value !== initial.isActive;
  }
}
